import type { ReactNode } from 'react';
import { FreePlayRules } from './freePlayRules';

/**
 * Web-side rules body content, keyed by week number.
 *
 * Per the project model (see .claude/STATUS.md — "Weekly rules"), the rules
 * BODY lives in web-side React; the API/admin surface only manages
 * publication metadata (title, version, effectiveFrom, published). RulesPage
 * looks up the body here by weekNumber and renders it inside the rules card.
 *
 * IMPORTANT — every week is a verbatim 1:1 transcription of its executed
 * Official Contest Rules document. Do not edit, paraphrase, re-order,
 * "improve", or derive the legal text or dates.
 *
 * TWO REVISIONS ARE LIVE AT ONCE. Legal reissued Episodes 3, 4 and 5 only
 * (set dated 8.11.2026, delivered 8.12.2026); Episodes 1 and 2 were NOT
 * reissued and their executed documents remain the 7.6.2026 set. Weeks 1-2
 * already carry thousands of recorded consents against that text, so their
 * body must keep rendering it. `EpisodeParams.revision` selects which of the
 * two texts a week renders:
 *
 *   '2026-07-06' — weeks 1-2. "GH5 2026 Neighborhood Swarm Contest_Ep N (7-6)",
 *                  the corrected set that fixes the air dates the 6.30 set
 *                  printed wrong (episodes air at the start of each promotion
 *                  period).
 *   '2026-08-11' — weeks 3-5. "GH5 2026 Neighborhood Swarm Contest_Ep N (8-11)".
 *                  Differs from the 7-6 text in exactly two places, both
 *                  anti-cheat, both marked REVISION DELTA below: the closing
 *                  paragraph of §6 and the opening of §10.a. Everything else —
 *                  dates, eligibility, prize, arbitration, winners' list — is
 *                  word-for-word identical to the 7-6 text (verified by
 *                  whitespace-normalized word-diff of both PDFs).
 *
 * Within a revision the documents are identical except for the episode number,
 * the promotion-period dates, and the summary air date, so the body stays a
 * single shared component (`EpisodeRules`) fed per-episode params from
 * `EPISODE_PARAMS`. Every param string is copied verbatim from its document —
 * including Ep 1's summary air date, which its document prints as
 * "July 31st, 2026" (ordinal "st").
 *
 * If a revised document lands: re-verify the shared body against it wholesale
 * (word-diff, not spot-check); add a new `revision` value rather than editing
 * an existing one whenever an already-consented week keeps the old text; and
 * bump that week's `version` in the weekly_rules row so prior consents are
 * correctly superseded. Ship the web deploy BEFORE the version bump — the body
 * lives in code and the version lives in the DB, so bumping first would record
 * consent against text the user could not yet have read.
 */

/** Which executed document revision a week's body renders. Identified by the
 *  date printed on the document set, not by the vendor's "V2/V4" folder
 *  labelling (which does not line up with the weekly_rules.version column). */
type RulesRevision = '2026-07-06' | '2026-08-11';

/** Per-episode values, verbatim from each executed document. */
type EpisodeParams = {
  /** Episode number as printed in the document (equals the week number). */
  episode: number;
  /** Promotion-period start date (summary block + §2). */
  periodStart: string;
  /** Promotion-period end date (summary block + §2). */
  periodEnd: string;
  /** Air date printed in the summary block ("which airs at approximately
   *  6:00 PM ET on …"). Verbatim per document — NOT derived from the
   *  promotion period (Ep 1 prints "July 31st" with the ordinal). */
  summaryAirDate: string;
  /** The executed document set this episode's body comes from. Weeks 1-2 were
   *  never reissued and stay on '2026-07-06'; weeks 3-5 were reissued on
   *  8.11.2026. See the REVISION DELTA blocks in `EpisodeRules`. */
  revision: RulesRevision;
};

const EPISODE_PARAMS: Record<number, EpisodeParams> = {
  1: { episode: 1, periodStart: 'July 7, 2026', periodEnd: 'August 6, 2026', summaryAirDate: 'July 31st, 2026', revision: '2026-07-06' },
  2: { episode: 2, periodStart: 'August 7, 2026', periodEnd: 'August 13, 2026', summaryAirDate: 'August 7, 2026', revision: '2026-07-06' },
  3: { episode: 3, periodStart: 'August 14, 2026', periodEnd: 'August 20, 2026', summaryAirDate: 'August 14, 2026', revision: '2026-08-11' },
  4: { episode: 4, periodStart: 'August 21, 2026', periodEnd: 'August 27, 2026', summaryAirDate: 'August 21, 2026', revision: '2026-08-11' },
  5: { episode: 5, periodStart: 'August 28, 2026', periodEnd: 'September 3, 2026', summaryAirDate: 'August 28, 2026', revision: '2026-08-11' },
};

/** Map of weekNumber → rules body renderer. Weeks without an entry fall back
 *  to the RulesPage placeholder. Visibility is still gated by the admin
 *  `published` flag, so an unpublished week shows the come-back-later
 *  placeholder regardless of this entry. */
export const WEEKLY_RULES_CONTENT: Record<number, () => ReactNode> = {
  1: () => <EpisodeRules {...EPISODE_PARAMS[1]} />,
  2: () => <EpisodeRules {...EPISODE_PARAMS[2]} />,
  3: () => <EpisodeRules {...EPISODE_PARAMS[3]} />,
  4: () => <EpisodeRules {...EPISODE_PARAMS[4]} />,
  5: () => <EpisodeRules {...EPISODE_PARAMS[5]} />,
  // Week 6 = post-contest Free Play (GHS5-85). Verbatim transcription of the
  // executed "JNS Free Play Official Rules_IMT Edits 8.13.26 18+" document
  // (version '2026-08-13'). Still gated behind published=false until ops
  // flips it at go-live. See content/freePlayRules.tsx.
  6: () => <FreePlayRules />,
};

/* ─── Shared presentational primitives ─────────────────────────────────── */

export const BODY = "font-['Arial',sans-serif] text-[clamp(13px,3.4vw,15px)] leading-[1.6] text-white/90";

export function Para({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={`${BODY} ${className ?? ''}`}>{children}</p>;
}

/** A numbered top-level rules clause: bold lead-in label + body. */
export function Clause({ n, label, children }: { n: number; label: string; children: ReactNode }) {
  return (
    <li className="flex gap-[10px]">
      <span className={`${BODY} shrink-0 font-bold`}>{n}.</span>
      <div className="flex flex-col gap-[12px]">
        <div className={BODY}>
          <span className="font-bold">{label}</span> {children}
        </div>
      </div>
    </li>
  );
}

/* ─── Episodes 1-5 — verbatim transcription ────────────────────────────────
 *  Weeks 1-2: executed 7.6.2026 set. Weeks 3-5: executed 8.11.2026 set.
 *  The only differences are the two REVISION DELTA blocks below.            */

function EpisodeRules({ episode, periodStart, periodEnd, summaryAirDate, revision }: EpisodeParams) {
  const isAug11 = revision === '2026-08-11';
  return (
    <div className="flex w-full flex-col gap-[18px]">
      {/* Short-form summary block (top of the executed document) */}
      <Para>
        The State Farm&reg; Gamerhood Jake&rsquo;s Neighborhood Swarm
        (&ldquo;Contest&rdquo;) will run from {periodStart} until {periodEnd}{' '}
        in connection with Episode {episode} of State Farm&rsquo;s Gamerhood
        Season 5, which airs at approximately 6:00 PM ET on {summaryAirDate}.
        NO PURCHASE OR
        PAYMENT OF ANY KIND IS NECESSARY TO ENTER OR WIN. Void where prohibited
        or restricted by law. Open to legal residents of the United States who
        are of majority age in their state of residence (19+ in AL, DE and NE
        and 21+ in MS) as of the start of the Contest. This is a promotion by
        Infinity Marketing Team, LLC. Twitch does not sponsor or endorse this
        promotion and is not responsible for it. YouTube does not sponsor or
        endorse this promotion and is not responsible for it. Secretlabs does
        not sponsor or endorse this promotion and is not responsible for it.
        Visit [https://statefarmgamerhood.com/rules].
      </Para>

      {/* Title block */}
      <div className="mt-[6px] text-center">
        <p className="font-['Orbitron',sans-serif] text-[clamp(14px,3.8vw,18px)] font-bold uppercase leading-[1.3] tracking-[0.04em] text-white">
          State Farm&reg; Gamerhood Jake&rsquo;s Neighborhood Swarm
        </p>
        <p className="font-['Orbitron',sans-serif] text-[clamp(13px,3.4vw,16px)] font-bold uppercase leading-[1.3] tracking-[0.04em] text-white">
          Official Contest Rules
        </p>
      </div>

      <Para className="font-bold">
        NO PURCHASE OR PAYMENT OF ANY KIND IS NECESSARY TO ENTER OR WIN. A
        PURCHASE DOES NOT INCREASE YOUR CHANCES OF WINNING A PRIZE. THIS CONTEST
        IS ONLY OPEN TO LEGAL RESIDENTS OF THE UNITED STATES WHO ARE OF MAJORITY
        AGE IN THEIR STATE OF RESIDENCE (19+ IN AL, DE AND NE AND 21+ IN MS) AS
        OF THE START OF THE CONTEST. DO NOT PARTICIPATE IF YOU DO NOT RESIDE IN
        THE UNITED STATES OR THE DISTRICT OF COLUMBIA AT THE TIME OF ENTRY AND
        ARE NOT OF MAJORITY AGE. VOID WHERE PROHIBITED OR RESTRICTED BY LAW.
        SUBJECT TO ALL APPLICABLE FEDERAL, STATE AND LOCAL LAWS AND REGULATIONS.
      </Para>

      <Para className="font-bold">
        ARBITRATION NOTICE: TO THE EXTENT PERMITTED BY LAW AND EXCLUDING ANY
        PARTICIPANTS RESIDING OR WORKING WITHIN THE STATES OF CALIFORNIA, NEW
        JERSEY OR NEW YORK, YOU AGREE THAT BY ENTERING THE CONTEST, YOU AGREE
        THAT DISPUTES BETWEEN YOU AND ANY CONTEST ENTITY (AS DEFINED HEREIN)
        WILL BE RESOLVED BY BINDING, INDIVIDUAL ARBITRATION AND YOU WAIVE YOUR
        RIGHT TO PARTICIPATE IN A JURY TRIAL, CLASS ACTION LAWSUIT OR CLASS-WIDE
        ARBITRATION. SEE DISPUTES/ARBITRATION PROVISION CONTAINED HEREIN.
      </Para>

      <ol className="flex flex-col gap-[14px]">
        <Clause n={1} label="GENERAL:">
          The State Farm Gamerhood Jake&rsquo;s Neighborhood Swarm (the
          &ldquo;Contest&rdquo;) is administered and sponsored by Infinity
          Marketing Team, LLC (&ldquo;Sponsor&rdquo;). The Contest is subject to
          these Official Rules and to all applicable federal, state, and local
          laws. Prize Provider: State Farm Mutual Automobile Insurance Company
          (&ldquo;State Farm&rdquo;), One State Farm Plaza, Bloomington, Illinois
          61710.
        </Clause>

        <Clause n={2} label="PROMOTION PERIOD:">
          The Contest will begin at 12:01 am Eastern Time (&ldquo;ET&rdquo;) on{' '}
          {periodStart} and will end at 11:59 pm ET on {periodEnd} (the
          &ldquo;Promotion Period&rdquo;), following the airing of Episode{' '}
          {episode} of Season 5 of the State Farm Gamerhood Challenge (the
          &ldquo;Broadcast&rdquo;). The official Broadcast can be accessed either
          on State Farm&rsquo;s Twitch stream at www.twitch.tv/statefarm/ or on
          State Farm&rsquo;s YouTube channel at www.youtube.com/statefarm.
          Eligible participants
          (&ldquo;participant&rdquo;, &ldquo;you&rdquo; or &ldquo;your&rdquo;)
          will be able to register for the Contest at any time during the
          Promotion Period as set forth in Section 4 of these Official Rules.
          Sponsor&rsquo;s computer is the official time- and score-keeping device
          for the Contest.
        </Clause>

        <Clause n={3} label="ELIGIBILITY:">
          The Contest is open only to legal residents of the fifty (50) United
          States and the District of Columbia, who, as of the start of the
          Promotion Period, are the age of majority or older in their state of
          residence (19+ in AL, DE and NE, 21+ in MS, and 18+ in all other
          states). Do not enter if you are not located in the United States or
          District of Columbia and are not the age of majority at the time of
          entry. Officers, directors, employees, agents and representatives of
          Sponsor, State Farm and each of their respective parent, subsidiaries,
          affiliated and related companies or agencies, and/or any person or
          company working directly on the Contest (collectively, the
          &ldquo;Contest Entities&rdquo;), together with the immediate family
          members and/or those living in the same household of such persons
          (collectively, &ldquo;Household Members&rdquo;), are not eligible to
          participate in the Contest or win a prize. For purposes of the Contest,
          &ldquo;Household Members&rdquo; shall mean those people who share the
          same residence at least three months a year, whether or not related;
          &ldquo;Immediate Family Members&rdquo; shall mean parents,
          step-parents, legal guardians, children, step-children, siblings,
          step-siblings, or spouses, regardless of where they live. Void where
          prohibited or restricted by law. All federal, state, and local laws and
          regulations apply. You are solely responsible for all costs or expenses
          associated with participation in the Contest.
        </Clause>

        <Clause n={4} label="HOW TO ENTER:">
          During the Promotion Period, you may enter the Contest on any
          internet-connected device by visiting https://statefarmgamerhood.com
          (the &ldquo;Website&rdquo;) and filling out the on-screen registration
          form. Normal internet access or device usage charges imposed by mobile
          carriers may apply to Contest participation via your mobile device,
          which shall be your sole responsibility. One entry per person. The
          authorized account holder of the email account provided during
          registration will be deemed to be the participant who entered. The
          Contest Entities are not liable for any failure of registration,
          regardless of cause or the person responsible, including, but not
          limited to, any problem or technical malfunction of any computer,
          online system, or server or technical problem. Registered participants
          who use inappropriate or offensive usernames (as determined by Sponsor
          in its sole discretion) will be disqualified from participation and
          will be ineligible to receive a prize. Sponsor will determine in its
          sole discretion all eligibility requirements in connection with the
          Contest.
        </Clause>

        <Clause n={5} label="CONTEST GAME PLAY:">
          During the Promotion Period, participants may compete (an unlimited
          number of times) in a game called Jake&rsquo;s Neighborhood Swarm, a
          platform game where you will, as Jake the player character, earn points
          by collecting coins and hearts, eliminating enemies, and defeating
          bosses, all while avoiding losing health from enemy strikes. As game
          play proceeds, Jake will advance through levels of increasing
          difficulty (e.g., faster enemies and more enemies to avoid). The object
          of the game is to acquire as many points as possible before Jake loses
          all of his health from enemy strikes. Game play and point accumulation
          will occur as follows:
          <Para className="mt-[12px]">
            Jake begins game play with four hearts, signifying full health. Each
            time Jake is struck by an enemy, he loses one heart. However, Jake can
            earn extra hearts during game play, restoring his health. Once Jake
            loses all four hearts, game play is over. At the conclusion of each
            game, you will receive a score based on a combination of: (i) coins
            collected (1 point each), (ii) hearts collected (1 point each), (iii)
            bosses defeated (1000 points each); (iv) a &ldquo;life bonus&rdquo;
            (the number of hearts you have when defeating a boss x 500); and (v)
            levels achieved (the number of levels you reach x 30). Your score is
            recorded into the database. If you play multiple times, the
            leaderboard (see Sec. 6) will automatically use your highest score for
            purposes of the Contest.
          </Para>
        </Clause>

        <Clause n={6} label="DETERMINATION OF WINNERS:">
          The eligible participants are the five (5) participants who, after
          conclusion of the Promotion Period achieved the five (5) highest scores
          during the Promotion Period (the &ldquo;Episode Winners&rdquo;) and will
          be awarded the prize set forth in Section 7, subject to confirmation of
          eligibility. In the event of a tie between eligible participants, all
          tying participants will be deemed the potential Episode Winners of the
          applicable prize. Sponsor will determine in its sole discretion which
          eligible entries have satisfied all eligibility requirements.
          <Para className="mt-[12px]">
            You are eligible to be an Episode Winner, subject to eligibility
            requirements, if you have not been awarded a prize in connection with
            any promotion conducted by the Contest Entities within the previous 45
            days.
          </Para>
          {/* REVISION DELTA 1 of 2 — §6 closing paragraph.
              The 8.11.2026 set rewrote this in all-caps, broadened it with the
              "or any other means of manipulating game play" catch-all, and
              reframed it from "are prohibited" to "constitute a violation of
              these Official Rules" (which is what hooks §10.a(ii)).
              Caps but NOT bold: the source PDF sets it in Calibri regular, the
              same face as §12's caps body — only the summary NO PURCHASE and
              ARBITRATION NOTICE blocks are Calibri-Bold. */}
          {isAug11 ? (
            <Para className="mt-[12px]">
              MULTIPLE ENTRIES (E.G., USE OF MULTIPLE EMAIL ADDRESSES), USE OF
              BOTS, COMPUTER SCRIPTS OR PROGRAMS AND OTHER AUTOMATED METHODS OF
              GAME PLAY, OR ANY OTHER MEANS OF MANIPULATING GAME PLAY,
              CONSTITUTE A VIOLATION OF THESE OFFICIAL RULES AND WILL VOID YOUR
              ELIGIBILITY TO WIN A PRIZE.
            </Para>
          ) : (
            <Para className="mt-[12px]">
              Multiple entries (e.g., use of multiple email addresses), and use of
              bots, computer scripts or programs and other automatic methods of game
              play, are prohibited and will void your eligibility to win a prize.
            </Para>
          )}
        </Clause>

        <Clause n={7} label="PRIZES:">
          During the Promotion Period, the top five (5) verified Episode Winners
          will receive the prize below:
          <Para className="mt-[12px] font-bold">
            Prize: Custom-Designed Secretlabs TITAN Evo Gaming Chair (ARV =
            $639.00)
          </Para>
        </Clause>

        <Clause n={8} label="CLAIMING THE PRIZE:">
          All potential Episode Winners will be notified at the email address
          provided at registration within 72 hours following the conclusion of
          the Promotion Period. All potential Episode Winners must respond to the
          initial notification from Sponsor within 72 hours after such
          notification is sent by Sponsor. If the potential Episode Winners do not
          timely respond or are deemed by Sponsor to be ineligible or not to have
          complied with these Official Rules, then the prize will be forfeited by
          such potential Episode Winners. Contest Entities are not responsible for
          any undelivered messages or any other communications, including but not
          limited to those that are not received because of failure of an email
          platform to function or any interruption in internet service. Upon
          verification of eligibility, verified Episode Winners will be provided
          with relevant information needed to redeem the prize.
        </Clause>

        <Clause n={9} label="TAXES:">
          Episode Winners are solely responsible for any and all federal, state,
          municipal and local taxes, fees, and other government assessments
          associated with receipt and use of a prize or participation in this
          Contest. Federal and state withholding laws are subject to change
          without notice.
        </Clause>

        <Clause n={10} label="GENERAL:">
          <ol className="mt-[12px] flex flex-col gap-[12px]">
            <li className="flex gap-[10px]">
              <span className={`${BODY} shrink-0 font-bold`}>a.</span>
              {/* REVISION DELTA 2 of 2 — §10.a opening.
                  The 8.11.2026 set moved "in its discretion" off the right and
                  onto the determination ("who, in Sponsor's sole discretion:"),
                  changed "the Official Rules" to "these Official Rules", and
                  added the forward-looking future-promotions ban. The rest of
                  the clause is unchanged. */}
              <span className={BODY}>
                {isAug11 ? (
                  <>
                    Sponsor reserves the right to disqualify any participant who,
                    in Sponsor&rsquo;s sole discretion: (i) tampers or attempts to
                    tamper with the operation of the Contest, including the entry
                    process or the Broadcast; (ii) violates these Official Rules;
                    or (iii) acts in an unsportsmanlike or disruptive manner, or
                    with intent to annoy, abuse, threaten or harass any other
                    person. Sponsor also reserves the right to disqualify any such
                    participant from participating in future promotions conducted
                    by Sponsor.{' '}
                  </>
                ) : (
                  <>
                    Sponsor reserves the right in its discretion to disqualify any
                    participant who: (i) tampers or attempts to tamper with the
                    operation of the Contest, including the entry process or the
                    Broadcast; (ii) violates the Official Rules; or (iii) acts in an
                    unsportsmanlike or disruptive manner, or with intent to annoy,
                    abuse, threaten or harass any other person.{' '}
                  </>
                )}
                In the event a participant attempts to deliberately damage the
                Broadcast or any associated website or platform, undermine the
                operation of the Contest, or otherwise violate any applicable laws
                in connection with such participant&rsquo;s participation in the
                Contest, Sponsor reserves the right to pursue all legal remedies
                available to it against such participant.
              </span>
            </li>
            <li className="flex gap-[10px]">
              <span className={`${BODY} shrink-0 font-bold`}>b.</span>
              <span className={BODY}>
                Contest Entities are not responsible for any incorrect or
                inaccurate information, whether caused by any Broadcast
                participants or users, human error, tampering, hacking or by any
                of the equipment or programming associated with or utilized in the
                Contest and assume no responsibility for any error, omission,
                interruption, deletion, defect, delay in operation or transmission
                (including availability or accessibility of the internet),
                incompatibility, communications failure, theft, loss or
                destruction of entries, nor for the failure to capture an entry or
                other information. Contest Entities are not responsible for injury
                or damage to participants&rsquo; or to any other person&rsquo;s
                phone, tablet, computer or other device related to or resulting
                from downloading materials from or use of the Broadcast or
                otherwise as a result of your participation in the Contest. If, for
                any reason, the Contest or any element thereof is not capable of
                running as planned by reason of, but not limited to, tampering,
                unauthorized intervention, fraud, force majeure, technical or other
                failures or errors, or any other causes similar or dissimilar which
                Sponsor deems, in its opinion, could corrupt or affect the
                administration, security, fairness, integrity or proper conduct of
                this Contest or any element thereof, Contest Entities reserve the
                right at their discretion to cancel, terminate, modify or suspend
                the Contest or any element thereof and select potential Episode
                Winners from non-suspect, eligible entries received prior to the
                action or as otherwise may be deemed fair and equitable by the
                Contest Entities. In the event of any discrepancy, ambiguity,
                inconsistency, printing or any other error or miscommunication in
                any communications, announcements, advertising or promotional
                materials relating to this Contest, these Official Rules shall
                govern. Contest Entities shall have no responsibility or obligation
                to a potential Episode Winner who is ineligible for the prize, or
                is unable to or who does not accept the prize, for any reason.
              </span>
            </li>
            <li className="flex gap-[10px]">
              <span className={`${BODY} shrink-0 font-bold`}>c.</span>
              <span className={BODY}>
                Prizes are awarded &ldquo;as is&rdquo; without any express or
                implied warranty or guarantee from Contest Entities, including but
                not limited to, the implied warranties of merchantability, fitness
                for a particular purpose and non-infringement. Only the prizes as
                described herein are available to be awarded, and in no event will
                the Contest Entities be responsible for awarding more or different
                prizes than stated herein. Prizes cannot be assigned, transferred,
                or substituted, except by Sponsor who may, at its discretion,
                substitute a prize or any part thereof with a prize of equal or
                greater value. Prizes are not redeemable for cash.
              </span>
            </li>
          </ol>
        </Clause>

        <Clause n={11} label="PRIVACY:">
          All information submitted by participants is subject to these Official
          Rules, and will be treated in a manner consistent with Sponsor&rsquo;s
          Privacy Policy and State Farm&rsquo;s Terms of Use and Privacy Policy.
          California residents only, please see: Do Not Sell or Share My Personal
          Information. By participating in the Contest, participants hereby agree
          that Sponsor and/or State Farm may collect and use their personal
          information in compliance with their respective Privacy Policies and
          acknowledge that they have read and accepted each such policy. By
          participating in the Contest, participants further agree and acknowledge
          that Sponsor and/or State Farm may share their personal information with
          third parties in order for the third party to market State Farm&rsquo;s
          products and services on behalf of State Farm, consistent with State
          Farm&rsquo;s Privacy Policy.
        </Clause>

        <Clause n={12} label="INDEMNIFICATION AND RELEASE OF LIABILITY:">
          BY PARTICIPATING, YOU AGREE TO THE FULLEST EXTENT PERMITTED BY LAW TO
          RELEASE, DISCHARGE AND HOLD HARMLESS TWITCH, YOUTUBE, SECRETLABS, THE
          CONTEST ENTITIES AND THEIR RESPECTIVE PARENT, AFFILIATES AND SUBSIDIARY
          COMPANIES, ADVERTISING AND PROMOTION AGENCIES, AND THEIR RESPECTIVE
          OFFICERS, MANAGERS, DIRECTORS, EMPLOYEES, INDEPENDENT CONTRACTORS,
          REPRESENTATIVES AND AGENTS (&ldquo;RELEASED PARTIES&rdquo;) FROM AND
          AGAINST ANY AND ALL ALLEGED OR ACTUAL CLAIMS, CAUSES OF ACTION, DEMANDS,
          LOSSES, SETTLEMENTS, LIABILITIES AND DAMAGES OF ANY KIND WHATSOEVER
          EXISTING NOW OR ARISING IN THE FUTURE (INCLUDING, WITHOUT LIMITATION,
          BODILY INJURY, PERSONAL INJURY, DEATH, DISABILITY AND PROPERTY DAMAGE,
          VIOLATION OF PROPRIETARY, PUBLICITY, PRIVACY OR ANY OTHER RIGHT), COSTS
          AND EXPENSES (INCLUDING, WITHOUT LIMITATION, REASONABLE ATTORNEYS&rsquo;
          FEES, COURT COSTS, SETTLEMENT AND DISBURSEMENTS) DIRECTLY OR INDIRECTLY
          ARISING OUT OF ANY USE OF YOUR ENTRY INTO THE CONTEST IN WHOLE OR IN
          PART; THE ACCEPTANCE, POSSESSION, USE OR MISUSE OF A PRIZE; PARTICIPATION
          IN ANY CONTEST OR PRIZE RELATED ACTIVITY; ACCESS TO THE BROADCAST; OR
          OTHER PARTICIPATION IN THIS CONTEST. By entering the Contest or accepting
          a prize, you covenant to the fullest extent permitted by law not to sue
          any Released Party or cause them to be sued regarding any matter released
          above, and not to disaffirm, limit or rescind this release. A waiver by
          one or more of the Contest Entities of any term in these Official Rules
          does not constitute a waiver of any other provision. SOME JURISDICTIONS
          DO NOT ALLOW CERTAIN LIMITATIONS OR EXCLUSION OF LIABILITY, SO THE
          FOREGOING MAY NOT APPLY TO EVERY PARTICIPANT. Any provision adjudged to
          be invalid shall be struck from these Official Rules and the remainder
          shall continue in full force and effect.
        </Clause>

        <Clause n={13} label="FORCE MAJEURE:">
          Contest Entities shall not be liable to participants, a potential or
          verified Episode Winner or any other person or entity for failure to
          execute the Contest, or supply a prize or any part thereof, by reason of
          any act of God, any action, regulation, order or request by any
          governmental or quasi-governmental entity (whether or not the action,
          regulation, order or request proves to be invalid), equipment failure,
          terrorist act, cyber-attack, earthquake, war, fire, flood, explosion,
          unusually severe weather, hurricane, epidemics, pandemics, embargo,
          labor dispute or strike (whether legal or illegal), labor or material
          shortage, transportation interruption of any kind, work slow-down, civil
          disturbance, insurrection, riot, cancellation or delay of any event, or
          any similar or dissimilar event beyond their reasonable control.
        </Clause>

        <Clause n={14} label="DISCLAIMERS:">
          Contest Entities are not responsible for lost, late, or misdirected
          entries, or telecommunication, internet, or hardware or software
          failures, including by reason of any bug or computer virus or other
          failure. Contest Entities may cancel, modify, or terminate the Contest
          (including winner selection and prizes) without prior notice if it is
          not capable of completion as planned, including by reason of infection
          of computer virus, tampering, unauthorized intervention, force majeure,
          or technical difficulties of any kind.
        </Clause>

        <Clause n={15} label="ALTERNATIVE DISPUTE RESOLUTION; ARBITRATION:">
          To the extent permitted by law and excluding any participants residing
          or working within the States of California, New Jersey or New York, any
          disputes relating to the terms of these Official Rules or the breach
          thereof, or connected with the Contest or the prizes awarded, shall be
          submitted to binding arbitration in Los Angeles, CA in accordance with
          the Commercial Arbitration rules of the American Arbitration Association
          (the &ldquo;AAA&rdquo;). Each party agrees that any dispute resolution
          proceedings will be conducted only on an individual basis and not in a
          class, consolidated or representative action. Each party hereby waives
          the right to participate in any class action, whether in court or
          arbitration. In the event that either party desires to arbitrate any
          such dispute, such party shall so notify the other party and the parties
          shall endeavor in good faith, for a period of 30 days, to resolve such
          dispute without arbitration. In the event that the parties cannot resolve
          the dispute within such 30 day period, then within 10 days thereafter,
          the parties shall jointly designate a single arbitrator to hear the
          dispute, or, if the parties are unable to jointly select an arbitrator,
          an arbitrator shall be chosen according to the rules of the AAA. The
          arbitration, including the rendering of the award, shall take place in
          Los Angeles, CA and shall be the exclusive forum for resolving such
          dispute, controversy, or claim. The decision of the arbitrator shall be
          final and binding upon the parties hereto. The decision of the arbitrator
          shall be executory, and judgment thereon may be entered by any court of
          competent jurisdiction. If for any reason a claim proceeds in court
          rather than in arbitration, each party waives any right to a jury trial,
          excluding in any civil matters brought within the States of California,
          New Jersey, or New York.
        </Clause>

        <Clause n={16} label="SEVERABILITY:">
          The invalidity, illegality, or unenforceability of any provision of
          these Official Rules shall in no way affect the validity, legality, or
          enforceability of any other provision hereof. Any invalid, illegal, or
          unenforceable provision shall be deemed severed from these Official
          Rules and the balance of these Official Rules shall be construed and
          enforced as if these Official Rules did not contain such invalid,
          illegal, or unenforceable provision.
        </Clause>

        <Clause n={17} label="WINNERS' LIST:">
          To obtain a list of Episode Winners, send an email to
          info@infinitymarketing.com within thirty (30) days of the end of the
          Promotion Period. The email should contain &ldquo;State Farm Gamerhood
          Jake&rsquo;s Neighborhood Swarm&rdquo; in the subject field. Or, send a
          stamped, self-addressed envelope to: &ldquo;State Farm Gamerhood
          Jake&rsquo;s Neighborhood Swarm&rdquo; c/o Infinity Marketing Team, LLC,
          8575 Higuera Street, Culver City, CA 90232. VT residents may omit return
          postage.
        </Clause>
      </ol>

      <Para className="mt-[6px]">
        &copy; 2026 Infinity Marketing Team, LLC. All rights reserved.
      </Para>
    </div>
  );
}
